import type { FilterLimitingConfig, FilterOption } from '@ui/shared/util-filter';
import type { Observable, OperatorFunction } from 'rxjs';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import type { FilterState } from '../services/filter-state.service';

/**
 * Setup an OperatorFunction that limits the incoming filter values list to those that are allowed,
 * depending on the limiting config and the currently selected values in other filters.
 *
 * If any of the selected filter values do not have an entry in the limiting config, it is assumed that all values should be allowed.
 *
 * @param limitingConfig The configuration that determines which values are allowed depending on the other filters' values
 * @param activeFilterService The active filter service used to determine the current values in the limiting filters.
 * @returns The values reduced to those that are allowed
 */
export const limitFilterValues$ = (
    limitingConfig: FilterLimitingConfig,
    activeFilterService: FilterState,
): OperatorFunction<Array<FilterOption>, Array<FilterOption>> => {
    const relevantKeys = Object.keys(limitingConfig);

    // Only update the active filters if any of the filters have changed that are listed in the limiting config.
    const activeFilters$ = activeFilterService.activeFilters$
        .pipe(
            distinctUntilChanged((previous, current) =>
                relevantKeys.every(key =>
                    JSON.stringify(previous.get(key)) === JSON.stringify(current.get(key)),
                ),
            ),
        );

    return (source$: Observable<Array<FilterOption>>) =>
        combineLatest([
            source$,
            activeFilters$,
        ])
            .pipe(
                map(([filterValues, activeFilters]) => {
                    // Change nothing if nothing is configured or no values are selected.
                    if (
                        !Object.keys(limitingConfig).length
                        || !activeFilters.size
                    ) {
                        return filterValues;
                    }

                    /*
                     * If any of the filters that are listed in the limiting config
                     * has a value selected that is itself not listed in the limiting config,
                     * assume that all values are allowed.
                     * This prevents situations where a limiting value and a non-limiting value are both selected,
                     * and only the limiting value is considered.
                     */
                    if (
                        Object.keys(limitingConfig)
                            .some(otherFilterKey => {
                                const otherFilterConfig = limitingConfig[otherFilterKey];

                                return activeFilters.get(otherFilterKey)?.values
                                    .some(filterValue =>
                                        !otherFilterConfig?.[filterValue.value],
                                    );
                            })
                    ) {
                        return filterValues;
                    }

                    let areValuesLimited = false;
                    const allowedValues = new Array<string | number>();

                    Object.keys(limitingConfig)
                        .forEach(otherFilterKey => {
                            const otherFilterValues = activeFilters.get(otherFilterKey)?.values;
                            const otherFilterConfig = limitingConfig[otherFilterKey];

                            // Do nothing if no values are selected or there is no matching entry for this filter in the config.
                            if (
                                otherFilterValues?.length
                                && otherFilterConfig
                                && Object.keys(otherFilterConfig).length
                            ) {
                                otherFilterValues.forEach(otherFilterValue => {
                                    const allowedForValue = otherFilterConfig[otherFilterValue.value];

                                    // Do nothing if there is no matching entry for this value in the config.
                                    if (!allowedForValue) {
                                        return;
                                    }

                                    allowedValues.push(...allowedForValue);
                                    areValuesLimited = true;
                                });
                            }
                        });

                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    if (!areValuesLimited) {
                        return filterValues;
                    }

                    return filterValues
                        .filter(filterValue => allowedValues.includes(filterValue.value));
                }),
            );
};
